<template>
  <loading-checkbox
    v-if="inputLoading"
    :label="topLabel"
    :background="background"
    :alt-bg="altBg"
  />

  <div v-else class="tw-flex tw-flex-col" :class="{ 'tw-w-full': optionList }">
    <base-input-label
      v-if="topLabel || tip"
      margin-bottom
      :id-for="inputId"
      :required="required"
      :label="topLabel"
      :tip="tip"
      :tip-hover="tipHover"
      :label-between="labelBetween"
    />

    <label
      class="tw-flex tw-items-center tw-group mod-button-transition tw-relative game-tile-radius"
      :class="[
        customLabelStyles,
        inputBgStyles,
        disabled ? 'tw-cursor-default tw-opacity-75' : 'tw-cursor-pointer',
        small ? 'tw-text-sm' : 'mod-input--text-size',
        {
          'tw-capitalize': capsCase,
          'mod-input--px mod-input--min-height': background,
          'tw-justify-center mod-input--height tw-w-full tw-font-bold mod-input--px':
            optionList,
        },
        errorStyles,
      ]"
      @keydown="handleKeyPress"
    >
      <!-- hidden input is used for tabing / focus -->
      <input
        class="tw-absolute tw-opacity-0 tw-size-0"
        :checked="checked"
        type="checkbox"
        :tabindex="disabled ? '-1' : '0'"
        @click="updateCheckbox"
      />
      <span
        class="tw-flex tw-items-center tw-justify-center tw-shrink-0 mod-button-transition"
        :class="[
          checkStyles,
          hasHover,
          optionList
            ? 'tw-absolute tw-top-0 tw-left-0 tw-size-full mod-global--border-radius'
            : 'tw-size-3 tw-rounded-sm tw-mr-2',
        ]"
      >
        <font-awesome-icon
          v-if="checked && !optionList"
          icon="check"
          class="tw-size-2"
        ></font-awesome-icon>
      </span>

      <slot v-if="$slots.label" name="label" />
      <span v-else-if="capsCase" class="tw-z-1">{{ label.toLowerCase() }}</span>
      <span v-else class="tw-z-1">{{ label }}</span>

      <span
        v-if="append !== undefined"
        class="tw-ml-auto tw-pl-1 tw-text-right tw-opacity-50"
      >
        {{ append }}
      </span>
    </label>

    <div
      v-if="hasErrors || hint"
      class="tw-flex tw-flex-col mod-hint-gap tw-space-y-1"
    >
      <base-input-errors :errors="errors" align="left" />

      <base-input-hint v-if="hint" :id="`${inputId}Help`">
        {{ hint }}
      </base-input-hint>
    </div>
  </div>
</template>

<script>
import LoadingCheckbox from '@components/Loading/LoadingCheckbox.vue'
import { genHtmlId, isBoolean } from '@helpers/utils.js'
import { computed, toRefs } from 'vue'

export default {
  components: { LoadingCheckbox },
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
    modelValue: {
      type: [Boolean, Number],
      default: undefined,
    },
    append: {
      type: [String, Number],
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    topLabel: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    tip: {
      type: String,
      default: '',
    },
    tipHover: {
      type: Boolean,
      default: false,
    },
    labelBetween: {
      type: Boolean,
      default: true,
    },
    hint: {
      type: String,
      default: '',
    },
    errors: {
      type: Array,
      default: () => [],
    },
    icon: {
      type: String,
      default: null,
    },
    background: {
      type: Boolean,
      default: true,
    },
    inputLoading: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    capsCase: {
      type: Boolean,
      default: false,
    },
    optionList: {
      type: Boolean,
      default: false,
    },
    altBg: {
      type: Boolean,
      default: false,
    },
    darkBg: {
      type: Boolean,
      default: false,
    },
    sidebar: {
      type: Boolean,
      default: false,
    },
    customLabelStyles: {
      type: String,
      default: '',
    },
  },
  emits: ['input'],
  setup(props, { emit }) {
    const {
      background,
      modelValue,
      optionList,
      disabled,
      sidebar,
      darkBg,
      errors,
      altBg,
      id,
    } = toRefs(props)

    const inputId = id.value ? id.value : genHtmlId()

    const checked = computed(
      () => modelValue.value === 1 || modelValue.value === true
    )

    const hasErrors = computed(() => !!errors.value?.length)

    const errorStyles = computed(() => {
      if (hasErrors.value) {
        return background.value
          ? 'tw-border-danger tw-border-2'
          : 'tw-text-danger'
      }
      return background.value && !disabled.value
        ? 'mod-input--focus-within'
        : ''
    })

    const hasHover = computed(() => {
      if (disabled.value || checked.value) {
        return ''
      }

      if (altBg.value) {
        return background.value ? '' : 'game-input-background-color'
      } else if (darkBg.value) {
        return ''
      } else {
        return background.value ? '' : 'game-input-background-color'
      }
    })

    const inputBgStyles = computed(() => {
      return background.value
        ? 'game-input-background-color'
        : {
            'tw-opacity-90 hover:tw-opacity-100 focus-within:tw-opacity-100':
              !disabled.value,
            'focus-within:game-link-color':
              !optionList.value && !disabled.value,
          }
    })

    const checkStyles = computed(() => {
      if (checked.value) {
        return 'game-primary-button-color game-primary-text'
      }
      if (altBg.value) {
        return background.value
          ? ''
          : `${
              sidebar.value ? 'tw-mobile-grey' : 'tw-bg-theme'
            } tw-opacity-40 group-hover:tw-opacity-100 group-focus:tw-opacity-100`
      } else if (darkBg.value) {
        return background.value
          ? ''
          : 'game-primary-button-color tw-opacity-70 group-hover:tw-opacity-100 group-focus:tw-opacity-100'
      } else {
        return 'tw-bg-[var(--input-background-alt-color)]'
      }
    })

    function updateCheckbox() {
      if (!disabled.value) {
        isBoolean(modelValue.value)
          ? emit('input', modelValue.value ? 0 : 1)
          : emit('input', modelValue.value ? false : true)
      }
    }

    function handleKeyPress(e) {
      switch (e.keyCode) {
        case 9: //tab
          break
        case 13: //return
        case 32: //space
          e.preventDefault()
          updateCheckbox()

          break
      }
    }

    return {
      handleKeyPress,
      updateCheckbox,
      inputBgStyles,
      checkStyles,
      errorStyles,
      hasErrors,
      hasHover,
      checked,
      inputId,
    }
  },
}
</script>
